import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Award, Heart, BookOpen, Users, Sparkles, CheckCircle, ArrowRight, Globe } from 'lucide-react';

const AboutPage = () => {
  const qualifications = [
    {
      icon: Users,
      title: 'Virš 10 metų patirtis',
      description: 'Vedu ir kuriu mokymus verslams (Girteka, IKEA, McDonald\'s, Vaikų linija ir kt.) bei atvirus mokymus EN, RU, LT kalbomis Lietuvoje ir užsienyje',
      color: 'bg-[#4CAF50]',
      lightColor: 'bg-[#E8F5E9]'
    },
    {
      icon: Award,
      title: 'ICC sertifikuota koučerė',
      description: 'Profesionali koučingo sertifikacija, leidžianti teikti aukščiausios kokybės konsultacijas',
      color: 'bg-[#FF9F43]',
      lightColor: 'bg-[#FFF3E0]'
    },
    {
      icon: Sparkles,
      title: 'Nuolatinis tobulėjimas',
      description: 'Aktyviai dalyvauju mokymuose Lietuvoje ir užsienyje. Esu baigusi ilgalaikius Hugo Alberts mokymų ciklus apie emocinį intelektą bei pozityviąją psichologiją',
      color: 'bg-[#5B9BD5]',
      lightColor: 'bg-[#E3F2FD]'
    },
    {
      icon: GraduationCap,
      title: 'Psichologijos studijos',
      description: '2026 vasarą baigsiu psichologijos bakalauro studijas',
      color: 'bg-[#9B59B6]',
      lightColor: 'bg-[#F3E5F5]'
    }
  ];

  const expertiseAreas = [
    { name: 'Emocinis intelektas', color: 'bg-[#E8F5E9] text-[#4CAF50]' },
    { name: 'Sudėtingos asmenybės', color: 'bg-[#F3E5F5] text-[#9B59B6]' },
    { name: 'Konfliktų valdymas', color: 'bg-[#FFEBEE] text-[#FF6B6B]' },
    { name: 'Emocinė sveikata', color: 'bg-[#FFF3E0] text-[#FF9F43]' },
    { name: 'Streso valdymas', color: 'bg-[#E3F2FD] text-[#5B9BD5]' },
    { name: 'Perdegimo sindromas', color: 'bg-[#FFEBEE] text-[#FF6B6B]' },
    { name: 'Viešasis kalbėjimas', color: 'bg-[#FFFDE7] text-[#F59E0B]' },
    { name: 'Suaugusiųjų mokymai', color: 'bg-[#E8F5E9] text-[#4CAF50]' }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-testid="hero-text">
              <div className="inline-flex items-center space-x-2 bg-[#E8F5E9] text-[#4CAF50] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                <span>Apie mane</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold leading-tight text-[#2D3748] mb-6">
                Sveiki! Esu <span className="text-[#4CAF50]">Rasa Borodina</span>
              </h1>
              <p className="text-lg text-[#718096] leading-relaxed mb-4">
                Mokymų trenerė, trijų knygų autorė, sertifikuota koučingo specialistė.
              </p>
              <p className="text-lg text-[#718096] leading-relaxed mb-8">
                > 10 metų vedu mokymus organizacijoms ir asmenims, padėdama jiems stiprinti emocinę sveikatą, gerinti bendravimo, konfliktų sprendimo bei emocijų reguliavimo įgūdžius.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/kontaktai"
                  data-testid="cta-contact-button"
                  className="inline-flex items-center justify-center bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-1 shadow-lg shadow-green-200"
                >
                  Susisiekite su manimi
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/sesijos"
                  className="inline-flex items-center justify-center bg-white text-[#2D3748] border-2 border-slate-200 hover:border-[#4CAF50] rounded-full px-8 py-4 font-semibold transition-all"
                >
                  Individualios sesijos
                </Link>
              </div>
            </div>

            {/* Profile Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/about/rasa-profile.jpg`}
                  alt="TavoEmocijos - Psichologė ir Koučerė"
                  className="w-full h-auto object-cover"
                />
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-slate-100">
                <div className="text-4xl font-bold text-[#4CAF50] mb-1">10+</div>
                <div className="text-sm text-[#718096]">Metų patirtis</div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#FFD93D] rounded-full opacity-60 -z-10"></div>
              <div className="absolute top-1/2 -right-8 w-16 h-16 bg-[#5B9BD5] rounded-full opacity-60 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-6">
            Mano misija
          </h2>
          <p className="text-xl text-[#718096] leading-relaxed mb-4">
            Padėti stiprinti emocinę sveikatą, išsiugdyti su emocijomis susijusius svarbiausius įgūdžius, išmokyti spręsti konfliktus ir valdyti stresą.
          </p>
          <p className="text-lg text-[#718096] leading-relaxed">
            Tikiu, kad mūsų gyvenimo džiaugsmas, pilnatvė, harmonija ir darbinis efektyvumas priklauso nuo gebėjimo reguliuoti savo emocijas. Taip pat mokau apie viešą kalbėjimą – svarbų įgūdį tiek profesinėje, tiek asmeninėje erdvėje.
          </p>
        </div>
      </section>

      {/* Qualifications */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-4">
              Kvalifikacijos ir patirtis
            </h2>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Nuolatinis mokymasis ir profesinis tobulėjimas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {qualifications.map((qual, index) => {
              const Icon = qual.icon;
              return (
                <div
                  key={index}
                  data-testid={`qualification-${index}`}
                  className="bg-white p-8 rounded-3xl border border-slate-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className={`w-14 h-14 ${qual.lightColor} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-7 h-7 ${qual.color.replace('bg-', 'text-')}`} />
                  </div>
                  <h3 className="text-xl font-['Poppins'] font-semibold mb-3 text-[#2D3748]">
                    {qual.title}
                  </h3>
                  <p className="text-[#718096] leading-relaxed">
                    {qual.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Expertise Areas */}
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100">
            <h3 className="text-2xl font-['Poppins'] font-semibold mb-8 text-[#2D3748] text-center">
              Mokymų temos
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {expertiseAreas.map((area, index) => (
                <span
                  key={index}
                  className={`${area.color} px-4 py-2 rounded-full text-sm font-medium`}
                >
                  {area.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Training Photos Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-4">
              Mokymų akimirkos
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/about/training-1.jpg`}
                alt="Mokymai su dalyviais"
                className="w-full h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/about/training-2.jpg`}
                alt="Vedama sesija"
                className="w-full h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/about/training-3.jpg`}
                alt="Mokymai konferencijų salėje"
                className="w-full h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/about/training-4.jpg`}
                alt="Mokymai lauke gamtoje"
                className="w-full h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-6">
            Dirbau su tokiomis organizacijomis
          </h2>
          <p className="text-lg text-[#718096] leading-relaxed">
            IKEA, McDonald's, Girteka, Vaikų linija, projektas „Aš ir Psichologija" ir kt.
          </p>
        </div>
      </section>

      {/* Why I Do This */}
      <section className="bg-[#E8F5E9] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-8 text-center">
            Kodėl užsiimu tuo, kuo užsiimu?
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <p className="text-lg text-[#718096] leading-relaxed mb-6">
              Psichologija mane lydi nuo pat paauglystės, kuomet pradėjau skaityti pirmąsias rimtas dalykines knygas. Šiuo metu tai man ne tik darbas, bet ir gyvenimo būdas. Džiaugiuosi galimybe kurti, ugdyti žmones ir tobulėti pati. Kaip ir visi, gyvenime patyriau skirtingų sunkumų, daug mokiausi ir stipriai investavau, visų pirma, į savo pačios įgūdžius.
            </p>
            <p className="text-lg text-[#718096] leading-relaxed">
              Drąsiai teigiu, kad tai, ko mokau žmones, esu patyrusi ir išbandžiusi pati. Esu praktikė, kuri tiki gilių pokalbių, ryšio ir tikrų pokyčių svarba. Koučingo konsultacijos man – tai galimybė padėti žmonėms suprasti save ir drąsiau žengti link savo tikslų, o mokymai - kaip puikus būdas uždegti žmonių širdis ir akis, įkvėpti pokyčiams.
            </p>
          </div>
        </div>
      </section>

      {/* Personal Side */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-8 text-center">
            Mano asmeninė pusė
          </h2>
          
          <div className="bg-[#FFF3E0] rounded-3xl p-8 md:p-12">
            <p className="text-lg text-[#718096] leading-relaxed mb-6">
              Gyvenime man svarbiausia yra šeima, sveikata, savirealizacija ir nuolatinis tobulėjimas. Mėgstu gamtą, stovyklavimą, aktyvų sportą, miškus ir jūrą, o kūryba užima vieną svarbiausių vietų mano gyvenime – esu parašiusi tris knygas.
            </p>
            <p className="text-lg text-[#718096] leading-relaxed">
              Man patinka dalintis savo patirtimi ir kurti erdvę, kurioje žmonės gali atrasti save ir jaustis įkvėpti.
            </p>
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="py-12 bg-[#FAFAF8]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-white rounded-3xl p-8 text-center border border-slate-100">
            <div className="w-16 h-16 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-6">
              <Globe className="w-8 h-8 text-[#5B9BD5]" />
            </div>
            <h3 className="text-2xl font-['Poppins'] font-semibold mb-4 text-[#2D3748]">
              Mokymus kuriu ir vedu trimis kalbomis
            </h3>
            <p className="text-lg text-[#718096] mb-6">
              Lietuvių, anglų ir rusų kalbomis visoje Lietuvoje bei užsienyje
            </p>
            <div className="flex justify-center gap-4">
              <span className="bg-[#E8F5E9] text-[#4CAF50] px-4 py-2 rounded-full text-sm font-medium">🇱🇹 Lietuvių</span>
              <span className="bg-[#E3F2FD] text-[#5B9BD5] px-4 py-2 rounded-full text-sm font-medium">🇬🇧 Anglų</span>
              <span className="bg-[#F3E5F5] text-[#9B59B6] px-4 py-2 rounded-full text-sm font-medium">🇷🇺 Rusų</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#4CAF50] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-white mb-6">
            Pradėkime kelionę kartu
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Susisiekite ir aptarkime, kaip galiu jums padėti
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontaktai"
              data-testid="cta-footer-contact"
              className="inline-flex items-center justify-center bg-white text-[#4CAF50] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
            >
              Susisiekti
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/mokymai"
              className="inline-flex items-center justify-center bg-white/10 text-white border-2 border-white hover:bg-white/20 rounded-full px-8 py-4 font-semibold transition-colors"
            >
              Peržiūrėti mokymus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
